import type { BusHandler, EventBus } from '@ekhodealer/ekho-common';

/**
 * EventTarget-backed pub/sub namespaced to the SDK — NOT `CustomEvent` on
 * `window`, which would pollute the host page's event space.
 *
 * Two dispatch modes:
 *
 * - `emit` — dispatch + buffer for replay. Use for one-shot lifecycle signals
 *   that late-loading modules need to read (e.g. `attribution:ready`).
 *
 * - `fire` — dispatch only, no buffering. Use for high-frequency or recurring
 *   events where replay would cause unbounded memory growth in long-lived SPA
 *   sessions (e.g. `page:context` on every navigation).
 *
 * `on` replays all previously `emit`-buffered payloads to a new subscriber,
 * in order. Idempotent — calling twice with the same handler won't double-register.
 */
export function createEventBus(): EventBus {
  const target = new EventTarget();
  const replayBuffer = new Map<string, unknown[]>();
  // Maps each (handler, event) pair to the wrapping EventListener so `off` can
  // remove the exact listener that `on` registered.
  const listeners = new WeakMap<BusHandler, Map<string, EventListener>>();

  function register(event: string, handler: BusHandler): EventListener {
    const listener: EventListener = (e) => handler((e as CustomEvent).detail);
    let byEvent = listeners.get(handler);
    if (!byEvent) {
      byEvent = new Map();
      listeners.set(handler, byEvent);
    }
    byEvent.set(event, listener);
    return listener;
  }

  /** Subscribe to an event. Replays all previously `emit`-buffered payloads immediately. */
  function on(event: string, handler: BusHandler): void {
    off(event, handler);
    target.addEventListener(event, register(event, handler));
    const buffered = replayBuffer.get(event);
    if (buffered) {
      for (const payload of buffered) handler(payload);
    }
  }

  /** Unsubscribe a specific handler from an event. */
  function off(event: string, handler: BusHandler): void {
    const listener = listeners.get(handler)?.get(event);
    if (!listener) return;
    target.removeEventListener(event, listener);
    listeners.get(handler)?.delete(event);
  }

  /** Dispatch + buffer for replay. Use for lifecycle signals (e.g. `attribution:ready`). */
  function emit(event: string, payload?: unknown): void {
    const buffered = replayBuffer.get(event) ?? [];
    const detail = payload ?? null;
    buffered.push(detail);
    replayBuffer.set(event, buffered);
    target.dispatchEvent(new CustomEvent(event, { detail }));
  }

  /** Dispatch only — no buffering. Use for recurring events (e.g. `page:context`). */
  function fire(event: string, payload?: unknown): void {
    const detail = payload ?? null;
    target.dispatchEvent(new CustomEvent(event, { detail }));
  }

  return { on, off, emit, fire };
}
